import React, { ReactNode, useState, useEffect } from "react";
import { useScrollTracker } from "../hooks/useScrollTracker";
import ReactDOM from "react-dom";
import { css } from "@emotion/css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import { faBookmark as faBookmarkOutline } from "@fortawesome/free-regular-svg-icons";
import { faBookmark as faBookmarkSolid } from "@fortawesome/free-solid-svg-icons";
import {
  faThumbsUp,
  faThumbsDown,
  faBan,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Popover } from "antd";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useRecommendationFeedStore } from "../../../stores/recommendationFeedStore";
import {
  storeUserActivity,
  addChannelToHiddenList,
} from "../api_client/recommendation_api";
import * as Sentry from "@sentry/react";
import { UserActivityType, ResourceType } from "../types/activity_types";
import {
  handleSeeMoreLike,
  handleSeeLessDislike,
  handleHideChannel,
} from "../utils/recommendation_actions_utils";

// Menu item interface for our options
interface MenuItemProps {
  icon: IconDefinition;
  text: string;
  onClick: (e: React.MouseEvent) => void;
  className?: string; // Optional custom class for active states
  iconClassName?: string; // Optional custom class for icon active states
  textClassName?: string; // Optional custom class for text active states
}

interface RecommendationActionsWrapperProps {
  children: ReactNode;
  recommendation?: any; // Direct access to the recommendation object
  channelName?: string;
  channelId?: string; // Channel ID for hiding functionality
  resourceId?: string; // ID of the resource / recommendation item
  resourceType?: ResourceType; // Type of resource
  onboardingMode?: boolean; // Whether the component is in onboarding mode
  onUnbookmark?: () => void; // Optional callback when item is unbookmarked
  hideMoreOptions?: boolean; // Whether to hide the more options button
}

const styles = {
  container: css`
    position: relative;
    width: 100%;
    margin-bottom: 1rem;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  `,
  debugInfoPanel: css`
    background-color: rgba(0, 0, 0, 0.8);
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.75rem;
    color: #00ff00;
    font-family: monospace;
    overflow-wrap: break-word;
  `,
  ytSharpBorder: css`
    // border: 1px solid #5c5c5c;
    border: none;
  `,
  videoBorder: css`
    border: none;
  `,
  ytSharpActionsContainer: (hideMoreOptions: boolean) => css`
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: ${hideMoreOptions ? "0" : "12px"};
    z-index: 10;
  `,
  videoCardActionsContainer: (hideMoreOptions: boolean) => css`
    position: absolute;
    bottom: 8px;
    right: 8px;
    display: flex;
    gap: ${hideMoreOptions ? "0" : "12px"};
    z-index: 10;
  `,
  actionIcon: css`
    background-color: rgba(0, 0, 0, 0.6);
    color: #ffffff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.8);
      transform: scale(1.1);
    }
  `,
  verticalEllipsis: css`
    transform: rotate(90deg);
  `,
  collapsing: css`
    animation: collapseItem 350ms ease-out forwards;
    overflow: hidden;
    pointer-events: none;

    @keyframes collapseItem {
      0% {
        opacity: 1;
        max-height: 1000px; /* Start with a height that can contain the content */
        margin-top: 16px;
        margin-bottom: 16px;
        transform: scale(1);
      }
      70% {
        opacity: 0.3;
        margin-top: 0;
        margin-bottom: 0;
        transform: scale(0.95);
      }
      100% {
        opacity: 0;
        max-height: 0;
        margin-top: 0;
        margin-bottom: 0;
        transform: scale(0.9);
      }
    }
  `,
  hidden: css`
    display: none;
  `,
  popoverMenuItem: css`
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: rgba(255, 255, 255, 0.9);

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    &:active {
      background-color: rgba(255, 255, 255, 0.2);
      color: #ffffff;
    }
  `,
  activeMenuItem: css`
    background-color: rgba(255, 255, 255, 0.14) !important;
  `,
  activeMenuIcon: css`
    color: #ffffff !important;
  `,
  activeMenuText: css`
    color: #ffffff !important;
  `,
  likedMenuIcon: css`
    color: #1890ff !important; /* Blue color for the liked icon */
  `,
  likedMenuText: css`
    color: #1890ff !important; /* Blue color for the liked text */
  `,
  popoverMenuIcon: css`
    margin-right: 12px;
    width: 16px;
    color: rgba(255, 255, 255, 0.9);
  `,
  popoverMenuText: css`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
  `,
  mobileMenu: css`
    width: 100%;
    padding: 0;
    margin: 0;
    background-color: #242424;
  `,
  mobileMenuItem: css`
    display: flex;
    align-items: center;
    padding: 16px;
    color: rgba(255, 255, 255, 0.9);
    transition: all 0.2s ease;
    cursor: pointer;

    &:active {
      background-color: rgba(255, 255, 255, 0.2);
      color: #ffffff;
    }
  `,
  mobileMenuIcon: css`
    margin-right: 16px;
    width: 20px;
    color: rgba(255, 255, 255, 0.9);
  `,
  mobileMenuText: css`
    font-size: 16px;
    color: rgba(255, 255, 255, 0.9);
  `,
};

// MobileModal component that uses React Portal
const MobileModal: React.FC<{
  items: MenuItemProps[];
  onClose: () => void;
}> = ({ items, onClose }) => {
  // TODO: Locking scroll (when the mobile modal/popover is open) doesn't quite work yet....
  // Simple scroll locking that preserves position
  useEffect(() => {
    // Save the original overflow value
    const originalOverflow = document.body.style.overflow;

    // Just disable scrolling without changing position
    document.body.style.overflow = "hidden";

    // Create a style element for additional CSS
    const styleEl = document.createElement("style");
    styleEl.id = "scroll-lock-style";
    styleEl.textContent = `
      body.scroll-locked {
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(styleEl);
    document.body.classList.add("scroll-locked");

    console.log("Scroll locked");

    // Cleanup function to unlock scrolling when unmounted
    return () => {
      // Restore original overflow
      document.body.style.overflow = originalOverflow;
      document.body.classList.remove("scroll-locked");

      // Remove the style element
      const styleToRemove = document.getElementById("scroll-lock-style");
      if (styleToRemove) {
        styleToRemove.remove();
      }

      console.log("Scroll unlocked");
    };
  }, []);

  const styles = {
    overlay: css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.45);
      z-index: 10000;
      display: flex;
      align-items: flex-end;
    `,
    bottomSheet: css`
      width: 100%;
      background-color: #242424;
      border-radius: 12px 12px 0 0;
      box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10001;
      animation: slideUp 0.3s ease-out forwards;

      @keyframes slideUp {
        from {
          transform: translateY(100%);
        }
        to {
          transform: translateY(0);
        }
      }
    `,
    header: css`
      display: flex;
      justify-content: flex-end;
      padding: 6px 14px;
    `,
    closeButton: css`
      background: none;
      border: none;
      cursor: pointer;
      font-size: 18px;
      color: rgba(255, 255, 255, 0.9);
      padding: 4px;
    `,
    // We don't need these styles anymore as we're using the shared ActionMenu component
  };

  // Use React Portal to render the modal directly into document.body
  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.bottomSheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <ActionMenu
            items={items.map((item) => ({
              ...item,
              onClick: (e) => {
                item.onClick(e);
                // onClose will be called after the delay in ActionMenu
              },
            }))}
            isMobile={true}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

// Shared menu component for both desktop and mobile
const ActionMenu: React.FC<{
  items: MenuItemProps[];
  isMobile: boolean;
}> = ({ items, isMobile }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const menuItemClass = isMobile
    ? styles.mobileMenuItem
    : styles.popoverMenuItem;
  const menuIconClass = isMobile
    ? styles.mobileMenuIcon
    : styles.popoverMenuIcon;
  const menuTextClass = isMobile
    ? styles.mobileMenuText
    : styles.popoverMenuText;

  // Reset activeIndex when items change (e.g., when isLiked changes)
  useEffect(() => {
    setActiveIndex(null);
  }, [items]);

  const handleItemClick = (
    item: MenuItemProps,
    index: number,
    e: React.MouseEvent
  ) => {
    // Set the active index to apply the visual feedback styles
    setActiveIndex(index);

    // For mobile, add a delay before executing the onClick handler
    if (isMobile) {
      // Prevent immediate propagation to allow visual feedback to be seen
      e.persist();

      // Delay the action to allow visual feedback to be visible
      setTimeout(() => {
        item.onClick(e);
        // Reset active index after action completes
        setTimeout(() => setActiveIndex(null), 50);
      }, 150); // 150ms delay for visual feedback
    } else {
      // For desktop, execute immediately
      item.onClick(e);
      // Reset active index after a short delay
      setTimeout(() => setActiveIndex(null), 50);
    }
  };

  return (
    <div className={isMobile ? styles.mobileMenu : undefined}>
      {items.map((item, index) => (
        <div
          key={index}
          className={`${menuItemClass} ${
            activeIndex === index ? styles.activeMenuItem : ""
          } ${item.className || ""}`}
          onClick={(e) => handleItemClick(item, index, e)}
        >
          <FontAwesomeIcon
            icon={item.icon}
            className={`${menuIconClass} ${
              activeIndex === index ? styles.activeMenuIcon : ""
            } ${item.iconClassName || ""}`}
          />
          <span
            className={`${menuTextClass} ${
              activeIndex === index ? styles.activeMenuText : ""
            } ${item.textClassName || ""}`}
          >
            {item.text}
          </span>
        </div>
      ))}
    </div>
  );
};

// Debug Info Panel component for displaying recommendation metadata
const DebugInfoPanel: React.FC<{
  recommendation: any;
}> = ({ recommendation }) => {
  if (!recommendation || !recommendation.enable_debug_ui) {
    return null;
  }

  // Additional styles for flex rows
  const flexRowStyle = {
    display: "flex",
    flexDirection: "row" as const,
    gap: "12px",
    marginBottom: "4px",
    flexWrap: "wrap" as const,
  };

  const flexItemStyle = {
    marginRight: "8px",
  };

  return (
    <div className={styles.debugInfoPanel}>
      {/* IDs section */}
      <div style={flexRowStyle}>
        {recommendation.id && (
          <div style={flexItemStyle}>
            Doc ID: {recommendation.id}
          </div>
        )}
        {recommendation.resource_id && (
          <div style={flexItemStyle}>
            Resource ID: {recommendation.resource_id}
          </div>
        )}
      </div>
      
      <hr />
      
      {/* First flex row - Scores */}
      <div style={flexRowStyle}>
        {recommendation.score !== null && (
          <div style={flexItemStyle}>
            Score: {recommendation.score.toFixed(3)}
          </div>
        )}
        {recommendation.resource_quality_score !== undefined && (
          <div style={flexItemStyle}>
            Quality: {recommendation.resource_quality_score.toFixed(2)}
          </div>
        )}
        {recommendation.recency_boost !== undefined &&
          recommendation.recency_boost !== null && (
            <div style={flexItemStyle}>
              Recency_boost: {recommendation.recency_boost.toFixed(2)}
            </div>
          )}
      </div>

      {recommendation.weighted_score !== undefined && (
        <div style={flexItemStyle}>
          Combined: {recommendation.weighted_score.toFixed(3)}
        </div>
      )}

      <hr />

      {/* Second flex row - Metadata */}
      <div style={flexRowStyle}>
        {recommendation.shelf_life && (
          <div style={flexItemStyle}>
            Shelf_life: {recommendation.shelf_life}
          </div>
        )}

        {recommendation.list_source && (
          <div style={flexItemStyle}>List: {recommendation.list_source}</div>
        )}
        {recommendation.cluster_key && (
          <div>
            Cluster: {recommendation.cluster_key.split("_")[1] || "none"}
          </div>
        )}
      </div>

      {recommendation.content_originally_published_on && (
        <div style={flexItemStyle}>
          Published:{" "}
          {new Date(
            recommendation.content_originally_published_on
          ).toLocaleDateString()}
        </div>
      )}

<hr />

      {recommendation.categories && recommendation.categories.length > 0 && (
        <div style={{...flexRowStyle, marginTop: '8px'}}>
          <div>Categories:</div>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
            {recommendation.categories.map((category: string, index: number) => (
              <span
                key={index}
                style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '12px',
                }}
              >
                - {category} 
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const RecommendationActionsWrapper: React.FC<
  RecommendationActionsWrapperProps
> = ({
  children,
  recommendation,
  channelName = "this channel",
  channelId = "",
  resourceId = "",
  resourceType = "yt_full_video",
  onboardingMode = false,
  onUnbookmark,
  hideMoreOptions = false,
}) => {
  // Determine derived_from_resource_id based on resource type
  const derivedResourceId = resourceType === ResourceType.YT_SHARP && recommendation?.resource_id 
    ? recommendation.resource_id 
    : "";

  const scrollTrackerRef = useScrollTracker({ 
    resourceId, 
    resourceType, 
    derived_from_resource_id: derivedResourceId
  });
  // Get the functions from our Zustand store
  const {
    hideChannel,
    isChannelHidden,
    isResourceBookmarked,
    isResourceLiked,
    addUserActivity,
    removeUserActivity,
    isResourceCollapsing,
    isResourceHidden,
    startCollapsingResource,
    hideResource,
  } = useRecommendationFeedStore();

  // Check if this channel is already hidden
  useEffect(() => {
    if (channelId && isChannelHidden(channelId)) {
      // If this channel is already in our hidden list, hide this component immediately
      if (resourceId) {
        hideResource(resourceId);
      }
    }
  }, [channelId, isChannelHidden, resourceId, hideResource]);
  const handleBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("Bookmark clicked for resource:", resourceId);
    console.log("Current bookmark status:", isResourceBookmarked(resourceId)); // using resource_id

    if (resourceId) {
      try {
        const response = await storeUserActivity(
          resourceId, // now document_id
          resourceType, // resource_type
          UserActivityType.BOOKMARKED, // activity_type
          derivedResourceId // derived_from_resource_id
        );

        if (response.success) {
          console.log("Full bookmark response:", response);
          if (response.soft_deleted === true) {
            // Activity was soft-deleted (toggled off)
            removeUserActivity(resourceId, UserActivityType.BOOKMARKED); // using resource_id, activity_type
            console.log("Bookmark removed:", response);
            // Call the onUnbookmark callback if provided
            if (onUnbookmark) {
              onUnbookmark();
            }
          } else {
            // Activity was added or toggled on
            addUserActivity({
              resource_id: resourceId,
              resource_type: resourceType as ResourceType,
              activity_type: UserActivityType.BOOKMARKED,
            });
            console.log("Bookmark added:", response);
          }
        }
      } catch (error) {
        console.error("Error toggling bookmark activity:", error);
        Sentry.captureException(error, {
          tags: {
            activity_type: UserActivityType.BOOKMARKED,
            resource_type: resourceType,
            resource_id: resourceId,
          },
        });
      }
    } else {
      const warning = "Cannot toggle bookmark activity: missing resourceId";
      console.warn(warning);
      Sentry.captureMessage(warning, {
        level: "warning",
        tags: { action: UserActivityType.BOOKMARKED },
      });
    }
  };

  const [popoverVisible, setPopoverVisible] = useState(false);
  const [mobileModalVisible, setMobileModalVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // We're now using the store for collapsing/hiding state instead of local state

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const handleMoreOptions = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) {
      setMobileModalVisible(true);
    } else {
      setPopoverVisible(!popoverVisible);
    }
  };

  const closeMenus = () => {
    setPopoverVisible(false);
    setMobileModalVisible(false);
  };

  const onSeeMoreLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Close menus immediately to prevent hover state from persisting
    closeMenus();

    // Use the abstracted utility function
    await handleSeeMoreLike({
      resourceId,
      resourceType,
      addUserActivity,
      removeUserActivity,
      isResourceLiked,
      onSuccess: () => {
        // Force close the popover again after state changes to reset any hover styling
        setTimeout(() => {
          setPopoverVisible(false);
        }, 50);
      },
      derived_from_resource_id: derivedResourceId
    });
  };

  // Check if the resource is bookmarked or liked for UI state
  const isBookmarked = resourceId ? isResourceBookmarked(resourceId) : false; // using resource_id
  const isLiked = resourceId ? isResourceLiked(resourceId) : false; // using resource_id

  const onSeeLessDislike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenus();

    // Use the abstracted utility function
    await handleSeeLessDislike({
      resourceId,
      resourceType,
      addUserActivity,
      removeUserActivity,
      startCollapsingResource,
      derived_from_resource_id: derivedResourceId
    });
  };

  const onHideChannel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    closeMenus();

    // Use the abstracted utility function
    await handleHideChannel({
      channelId,
      channelName,
      resourceId,
      hideChannel,
      startCollapsingResource
    });
  };

  // Define menu items once to be used in both desktop and mobile
  // This needs to be inside the component to access the latest isLiked state
  const getMenuItems = (): MenuItemProps[] => [
    {
      icon: faThumbsUp,
      text: "See more items like this",
      onClick: onSeeMoreLike,
      // Add blue color for the liked state without background change
      className: "",
      iconClassName: isLiked ? `${styles.likedMenuIcon}` : "",
      textClassName: isLiked ? `${styles.likedMenuText}` : "",
    },
    {
      icon: faThumbsDown,
      text: "See less items like this",
      onClick: onSeeLessDislike,
    },
    {
      icon: faBan,
      text: `Hide all items from ${channelName}`,
      onClick: onHideChannel,
    },
  ];

  // Get the menu items with the current state
  const menuItems = getMenuItems();

  // Add animation classes based on state from the store
  const containerClasses = [
    styles.container,
    resourceType === ResourceType.YT_SHARP
      ? styles.ytSharpBorder
      : styles.videoBorder,
    resourceId && isResourceCollapsing(resourceId) ? styles.collapsing : "",
    resourceId && isResourceHidden(resourceId) ? styles.hidden : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Don't render if hidden in the store
  if (resourceId && isResourceHidden(resourceId)) return null;

  return (
    <div ref={scrollTrackerRef} className={containerClasses}>
      {/* Render the debug panel first if applicable */}
      {recommendation && !onboardingMode && (
        <DebugInfoPanel recommendation={recommendation} />
      )}

      {/* Render the actual recommendation content */}
      <div className="recommendation-content">
        {children}

        {/* Add action controls */}
        <div
          className={
            resourceType === ResourceType.YT_SHARP
              ? styles.ytSharpActionsContainer(hideMoreOptions)
              : styles.videoCardActionsContainer(hideMoreOptions)
          }
        >
          {!onboardingMode && (
            <>
              <div className={styles.actionIcon} onClick={handleBookmark}>
                <FontAwesomeIcon
                  icon={isBookmarked ? faBookmarkSolid : faBookmarkOutline}
                />
              </div>
              <Popover
                overlayInnerStyle={{
                  backgroundColor: "#242424",
                  borderRadius: "8px",
                }}
                content={<ActionMenu items={menuItems} isMobile={false} />}
                trigger="click"
                open={popoverVisible && !isMobile}
                onOpenChange={(visible) => setPopoverVisible(visible)}
                placement="bottomRight"
                arrow={false}
              >
                {!hideMoreOptions && (
                  <div
                    className={styles.actionIcon}
                    onClick={handleMoreOptions}
                  >
                    <FontAwesomeIcon
                      icon={faEllipsisH}
                      className={styles.verticalEllipsis}
                    />
                  </div>
                )}
              </Popover>
            </>
          )}

          {/* Render the MobileModal via Portal when visible */}
          {isMobile && mobileModalVisible && (
            <MobileModal
              items={menuItems}
              onClose={() => setMobileModalVisible(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationActionsWrapper;
